import Reveal from "@/components/Reveal";
import { IMAGES } from "@/lib/constants";

const SERVICES = [
  {
    no: "01",
    title: "Land & Housing Development",
    image: IMAGES.exterior1,
    intro: "Residential, commercial, and mixed-use development across Alberta and the Caribbean — from land acquisition through delivery, including 3D and virtual renderings so you can see the outcome before we build it.",
    bullets: [
      "Residential & small-to-mid-scale housing projects",
      "Commercial property development",
      "3D and virtual pre-construction renderings",
      "Renovation and redevelopment of distressed properties",
      "Rezoning, subdivision, servicing coordination",
    ],
  },
  {
    no: "02",
    title: "Property Acquisition & Flipping",
    image: IMAGES.exterior2,
    intro: "Identify undervalued and abandoned properties, execute the renovation, and exit at the right time. Target 20% – 40% ROI per project.",
    bullets: [
      "Distressed & abandoned property sourcing",
      "Market analysis, feasibility & ROI modelling",
      "Below-market acquisition & due diligence",
      "Renovation planning, contractor sourcing, construction oversight",
      "Resale strategy or hold-and-rent conversion",
    ],
  },
  {
    no: "03",
    title: "International Investor Services",
    image: IMAGES.luxury,
    intro: "Full-cycle representation for investors abroad — property sourcing, legal coordination, project management, and quarterly reporting. Available as a flat fee (US$15K – US$50K) or 5% – 10% of total investment.",
    bullets: [
      "Property sourcing for foreign investors",
      "Legal & regulatory guidance via vetted partners",
      "Currency, tax, and repatriation planning",
      "Development oversight & progress reporting",
      "Premium investor concierge packages",
    ],
  },
  {
    no: "04",
    title: "Hotels & Villas",
    image: IMAGES.villaHero,
    intro: "Ground-up boutique villa and hotel development — currently active in Canelles, St. Lucia. Designed for high-yield short-term rental and long-hold appreciation.",
    bullets: [
      "Site selection & subdivision planning",
      "Architectural & interior design coordination",
      "Ground-up construction management",
      "Hospitality operations & rental setup",
      "Cross-border investor structures",
    ],
  },
  {
    no: "05",
    title: "Project Management & Consulting",
    image: IMAGES.project1,
    intro: "Single point of accountability across the entire development lifecycle. Independent advisory for investors entering or expanding in North America and the Caribbean.",
    bullets: [
      "Consultant, trade & municipal coordination",
      "Schedule, scope & cost control",
      "Quality, finish & hand-over oversight",
      "Market analysis & portfolio strategy",
      "Ongoing property management (8% – 12% of rent)",
    ],
  },
];

export default function Services({ onOpenConsult }) {
  return (
    <div data-testid="services-page" className="bg-[#0A1628]">
      {/* HEADER */}
      <section data-testid="services-header" className="pt-40 pb-20 md:pb-28 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-2">
            <div className="overline">Services</div>
          </div>
          <div className="lg:col-span-10">
            <h1 className="font-serif-display text-6xl md:text-8xl lg:text-[9rem] text-[#F3EFE4] leading-[0.88]">
              Full-service<br />real estate, <span className="italic text-[#D4AF37]">end to end.</span>
            </h1>
            <p className="mt-10 max-w-xl text-[#8FA3B8] leading-relaxed text-base md:text-lg">
              A single firm, five disciplines, one accountable point of contact — from Alberta land assemblies to boutique villa developments in the Caribbean. Your investment should not require five different vendors to succeed.
            </p>
          </div>
        </div>
      </section>

      <div className="hairline max-w-[1400px] mx-auto" />

      {/* SERVICE BLOCKS — alternating */}
      <section data-testid="services-blocks" className="py-12 md:py-20 px-6 md:px-12 lg:px-16">
        <div className="max-w-[1400px] mx-auto space-y-20 md:space-y-32">
          {SERVICES.map((s, i) => (
            <Reveal key={s.no}>
              <div className="grid lg:grid-cols-12 gap-12 items-center">
                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <div className="zoom-wrap aspect-[4/3] w-full">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                </div>
                <div className={`lg:col-span-5 ${i % 2 === 1 ? "lg:order-1 lg:col-start-2" : "lg:col-start-8"}`}>
                  <div className="overline mb-4">Service {s.no}</div>
                  <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#F3EFE4] leading-[0.95] mb-6">
                    {s.title}
                  </h2>
                  <p className="text-[#8FA3B8] leading-relaxed mb-8">{s.intro}</p>
                  <ul className="space-y-3 mb-10">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-4 text-sm text-[#F3EFE4]">
                        <span className="mt-2 inline-block w-3 h-px bg-[#D4AF37] shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <button data-testid={`service-cta-${s.no}`} onClick={onOpenConsult} className="btn-link">
                    Discuss this service <span>→</span>
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section data-testid="services-cta" className="py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <div className="overline mb-6">Next step</div>
            <h2 className="font-serif-display text-4xl md:text-6xl text-[#F3EFE4] leading-[0.95]">
              Not sure which service fits?<br />
              <span className="italic text-[#D4AF37]">Let's talk it through.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <button data-testid="services-cta-btn" onClick={onOpenConsult} className="btn-primary">
              Book Consultation <span className="btn-arrow">→</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
